import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePendingItems, relativeTime, PendingItem } from '../../hooks/usePendingItems';

type Filter = 'all' | 'ticket' | 'verification';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'verification', label: 'Vérifications' },
  { value: 'ticket', label: 'Support' }
];

const KIND_LABEL: Record<PendingItem['kind'], string> = {
  ticket: 'Ticket de support',
  verification: 'Document à vérifier'
};

const TicketIcon = () => (
  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8M8 14h5m7-2a8 8 0 01-8 8H7l-4 3V12a8 8 0 018-8h1a8 8 0 018 8z" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

/**
 * Tout ce qui attend une action, sur une page à part.
 *
 * La cloche n'en montre que les trois plus anciens : elle sert à savoir s'il y
 * a urgence. Traiter une file demande de la place — de quoi filtrer, comparer
 * les délais, et voir d'un coup d'œil ce qui traîne depuis des jours.
 */
const PendingQueue: React.FC = () => {
  const navigate = useNavigate();
  const { items, loading } = usePendingItems();
  const [filter, setFilter] = useState<Filter>('all');

  const visible = useMemo(
    () => (filter === 'all' ? items : items.filter((item) => item.kind === filter)),
    [items, filter]
  );

  const countByKind = useMemo(
    () => ({
      ticket: items.filter((item) => item.kind === 'ticket').length,
      verification: items.filter((item) => item.kind === 'verification').length
    }),
    [items]
  );

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {FILTERS.map((entry) => {
          const count =
            entry.value === 'all'
              ? items.length
              : countByKind[entry.value as 'ticket' | 'verification'];
          const active = filter === entry.value;
          return (
            <button
              key={entry.value}
              type="button"
              onClick={() => setFilter(entry.value)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                active ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              {entry.label}
              <span className={active ? 'ml-1.5 opacity-80' : 'ml-1.5 text-gray-400'}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        /* Squelette calqué sur la ligne réelle : icône, deux lignes de texte,
           horodatage et chevron restent à leur place quand les données arrivent. */
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
          {Array.from({ length: 5 }).map((_, index) => (
            <li key={index} className="flex items-center gap-4 px-5 py-4">
              <span className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-gray-100" />

              <span className="min-w-0 flex-1">
                <span className="mb-1.5 block h-3.5 w-48 max-w-full animate-pulse rounded bg-gray-100" />
                <span className="block h-3 w-32 max-w-full animate-pulse rounded bg-gray-100" />
              </span>

              <span className="h-3 w-14 shrink-0 animate-pulse rounded bg-gray-100" />
              <span className="h-4 w-4 shrink-0 animate-pulse rounded bg-gray-100" />
            </li>
          ))}
        </ul>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl bg-white py-20 text-center text-sm text-gray-500 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
          {filter === 'all'
            ? "Rien n'attend d'être traité."
            : 'Rien à traiter dans cette catégorie.'}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
          {visible.map((item) => (
            <li key={`${item.kind}:${item.id}`}>
              <button
                type="button"
                onClick={() => navigate(item.target)}
                className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                    item.kind === 'ticket' ? 'bg-orange-50 text-orange-600' : 'bg-indigo-50 text-indigo-600'
                  }`}
                >
                  {item.kind === 'ticket' ? <TicketIcon /> : <DocumentIcon />}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{item.title}</span>
                  <span className="block truncate text-xs text-gray-500">
                    {KIND_LABEL[item.kind]} · {item.subtitle}
                  </span>
                </span>

                <span className="shrink-0 whitespace-nowrap text-xs text-gray-400">
                  {relativeTime(item.date)}
                </span>

                <svg
                  className="h-4 w-4 shrink-0 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PendingQueue;
